import { FieldAssignment, normalizeAssignments } from './smartFieldMarkers';

/**
 * Adjusts field assignments when clean content changes (lines added/removed).
 *
 * Finds the common prefix and suffix of unchanged lines, then maps each
 * assignment boundary to the corresponding position in the new content:
 *
 * - Lines before the change region keep their index.
 * - Lines after the change region shift by the line-count delta.
 * - Lines inside the change region are clamped to the new region boundaries
 *   so they never leak into surrounding unchanged lines (e.g. headings).
 */
export const adjustAssignmentsForContentChange = (
    oldContent: string,
    newContent: string,
    assignments: FieldAssignment[]
): FieldAssignment[] => {
    if (assignments.length === 0) {
        return assignments;
    }

    const oldLines = oldContent.split('\n');
    const newLines = newContent.split('\n');
    const delta = newLines.length - oldLines.length;

    if (delta === 0) {
        return assignments;
    }

    // Find the first differing line from the start
    let commonPrefixLength = 0;
    const minLength = Math.min(oldLines.length, newLines.length);
    while (commonPrefixLength < minLength && oldLines[commonPrefixLength] === newLines[commonPrefixLength]) {
        commonPrefixLength += 1;
    }

    // Find the first differing line from the end
    let commonSuffixLength = 0;
    while (
        commonSuffixLength < minLength - commonPrefixLength &&
        oldLines[oldLines.length - 1 - commonSuffixLength] === newLines[newLines.length - 1 - commonSuffixLength]
    ) {
        commonSuffixLength += 1;
    }

    // Change regions: [commonPrefixLength .. oldChangeEnd) in old content,
    //                  [commonPrefixLength .. newChangeEnd) in new content.
    const oldChangeEnd = oldLines.length - commonSuffixLength;
    const newChangeEnd = newLines.length - commonSuffixLength;

    const mapLine = (line: number): number => {
        if (line < commonPrefixLength) {
            return line; // before change — unchanged
        }
        if (line >= oldChangeEnd) {
            return line + delta; // after change — shift
        }
        // Inside the changed region — clamp to the new region boundaries
        // so the line never leaks into the unchanged prefix or suffix.
        return Math.max(commonPrefixLength, Math.min(line + delta, newChangeEnd - 1));
    };

    return assignments
        .map((assignment) => {
            const adjustedStart = mapLine(assignment.startLine);
            const adjustedEnd = mapLine(assignment.endLine);

            if (adjustedStart > adjustedEnd || adjustedEnd < 0) {
                return null;
            }

            return {
                ...assignment,
                startLine: Math.max(0, adjustedStart),
                endLine: Math.max(0, adjustedEnd),
            };
        })
        .filter((assignment): assignment is FieldAssignment => assignment !== null);
};

export interface FieldTint {
    base: string;
    active: string;
    activeRing: string;
    activeEdge: string;
    activePreviewRing: string;
}

export interface ActiveResizeState {
    code: string;
    startLine: number;
    endLine: number;
    edge: 'start' | 'end';
}

export interface ActiveMoveState {
    code: string;
    startLine: number;
    endLine: number;
    pointerOffset: number;
}

export interface LineMetric {
    top: number;
    bottom: number;
    height: number;
}

export const FIELD_TINTS = [
    {
        base: 'border-emerald-200 bg-emerald-50/80',
        active: 'border-emerald-300 bg-emerald-100/90',
        activeRing: 'ring-emerald-200/80',
        activeEdge: 'rgba(110, 231, 183, 0.95)',
        activePreviewRing: 'ring-emerald-400',
    },
    {
        base: 'border-sky-200 bg-sky-50/80',
        active: 'border-sky-300 bg-sky-100/90',
        activeRing: 'ring-sky-200/80',
        activeEdge: 'rgba(125, 211, 252, 0.95)',
        activePreviewRing: 'ring-sky-400',
    },
    {
        base: 'border-amber-200 bg-amber-50/80',
        active: 'border-amber-300 bg-amber-100/90',
        activeRing: 'ring-amber-200/80',
        activeEdge: 'rgba(252, 211, 77, 0.95)',
        activePreviewRing: 'ring-amber-400',
    },
    {
        base: 'border-rose-200 bg-rose-50/80',
        active: 'border-rose-300 bg-rose-100/90',
        activeRing: 'ring-rose-200/80',
        activeEdge: 'rgba(253, 164, 175, 0.95)',
        activePreviewRing: 'ring-rose-400',
    },
    {
        base: 'border-violet-200 bg-violet-50/80',
        active: 'border-violet-300 bg-violet-100/90',
        activeRing: 'ring-violet-200/80',
        activeEdge: 'rgba(196, 181, 253, 0.95)',
        activePreviewRing: 'ring-violet-400',
    },
    {
        base: 'border-cyan-200 bg-cyan-50/80',
        active: 'border-cyan-300 bg-cyan-100/90',
        activeRing: 'ring-cyan-200/80',
        activeEdge: 'rgba(103, 232, 249, 0.95)',
        activePreviewRing: 'ring-cyan-400',
    },
] as const satisfies readonly FieldTint[];

export const escapeHtml = (text: string) => text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const renderInlineMarkdown = (text: string) => {
    const escaped = escapeHtml(text);

    return escaped
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/__([^_]+?)__/g, '<strong>$1</strong>')
        .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
        .replace(/_([^_]+?)_/g, '<em>$1</em>')
        .replace(/`([^`]+?)`/g, '<code class="rounded bg-black/5 px-1 py-0.5 text-[0.92em]">$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="underline decoration-current/30">$1</a>');
};

export const getVisualLineDescriptor = (line: string) => {
    const headingMatch = line.match(/^(#{1,4})\s+(.*)$/);
    if (headingMatch) {
        return {
            kind: 'heading' as const,
            html: renderInlineMarkdown(headingMatch[2]),
            level: headingMatch[1].length,
        };
    }

    const unorderedListMatch = line.match(/^(\s*)[*-]\s+(.*)$/);
    if (unorderedListMatch) {
        return {
            kind: 'unordered-list' as const,
            html: renderInlineMarkdown(unorderedListMatch[2]),
            indent: Math.floor((unorderedListMatch[1] || '').length / 2),
        };
    }

    const orderedListMatch = line.match(/^(\s*)(\d+)\.\s+(.*)$/);
    if (orderedListMatch) {
        return {
            kind: 'ordered-list' as const,
            html: renderInlineMarkdown(orderedListMatch[3]),
            indent: Math.floor((orderedListMatch[1] || '').length / 2),
            marker: orderedListMatch[2],
        };
    }

    const quoteMatch = line.match(/^>\s+(.*)$/);
    if (quoteMatch) {
        return {
            kind: 'quote' as const,
            html: renderInlineMarkdown(quoteMatch[1]),
        };
    }

    if (!line.trim()) {
        return {
            kind: 'blank' as const,
            html: '',
        };
    }

    return {
        kind: 'paragraph' as const,
        html: renderInlineMarkdown(line),
    };
};

// Blanco regels die alleen structurele markdown-opmaak zijn (rondom koppen of
// tussen twee lijstitems) krijgen in de uiteindelijke weergave geen hoogte
// (zie collapseListItemBlankLines). Klap ze in de overlay dus ook in, zodat de
// overlay hetzelfde oogt als de gerenderde tekst.
export const isStructuralBlankLine = (lines: string[], index: number): boolean => {
    if ((lines[index] ?? '').trim() !== '') {
        return false;
    }

    const findNeighborKind = (step: -1 | 1) => {
        for (let i = index + step; i >= 0 && i < lines.length; i += step) {
            if ((lines[i] ?? '').trim() === '') {
                continue;
            }
            return getVisualLineDescriptor(lines[i]).kind;
        }
        return null;
    };

    const prevKind = findNeighborKind(-1);
    const nextKind = findNeighborKind(1);

    if (prevKind === 'heading' || nextKind === 'heading') {
        return true;
    }

    const isListKind = (kind: string | null) => kind === 'unordered-list' || kind === 'ordered-list';
    return isListKind(prevKind) && isListKind(nextKind);
};

export const getFieldTint = (code: string): FieldTint => {
    const total = code.split('').reduce((sum, character) => sum + character.charCodeAt(0), 0);
    return FIELD_TINTS[total % FIELD_TINTS.length];
};

export const getAssignmentPreview = (lines: string[], assignment: FieldAssignment) => {
    const previewLine = lines
        .slice(assignment.startLine, assignment.endLine + 1)
        .find((line) => line.trim().length > 0);

    if (!previewLine) {
        return 'Lege sectie';
    }

    const normalized = previewLine.replace(/^#{1,4}\s+/, '').trim();
    return normalized.length > 72 ? `${normalized.slice(0, 69)}...` : normalized;
};

export const isSameAssignment = (
    left: Pick<FieldAssignment, 'code' | 'startLine' | 'endLine'>,
    right: Pick<FieldAssignment, 'code' | 'startLine' | 'endLine'>
) => left.code === right.code && left.startLine === right.startLine && left.endLine === right.endLine;

export const isSameResizeState = (left: ActiveResizeState | null, right: ActiveResizeState | null) => {
    if (!left || !right) {
        return false;
    }

    return left.edge === right.edge && isSameAssignment(left, right);
};

export const isSameMoveState = (left: ActiveMoveState | null, right: ActiveMoveState | null) => {
    if (!left || !right) {
        return false;
    }

    return left.pointerOffset === right.pointerOffset && isSameAssignment(left, right);
};

export const resizeAssignmentRange = (
    currentAssignments: FieldAssignment[],
    targetAssignment: Pick<FieldAssignment, 'code' | 'startLine' | 'endLine'>,
    edge: 'start' | 'end',
    lineIndex: number,
    lineCount: number
): { nextAssignments: FieldAssignment[]; nextActiveResize: ActiveResizeState | null } => {
    const normalizedAssignments = normalizeAssignments(currentAssignments, lineCount);
    const assignmentIndex = normalizedAssignments.findIndex((assignment) => isSameAssignment(assignment, targetAssignment));

    if (assignmentIndex === -1) {
        return { nextAssignments: normalizedAssignments, nextActiveResize: null };
    }

    const assignment = normalizedAssignments[assignmentIndex];
    const previousAssignment = assignmentIndex > 0 ? normalizedAssignments[assignmentIndex - 1] : null;
    const nextAssignment = assignmentIndex < normalizedAssignments.length - 1 ? normalizedAssignments[assignmentIndex + 1] : null;

    let nextStart = assignment.startLine;
    let nextEnd = assignment.endLine;

    if (edge === 'start') {
        const minimumStart = previousAssignment ? previousAssignment.endLine + 1 : 0;
        nextStart = Math.max(minimumStart, Math.min(lineIndex, assignment.endLine));
    } else {
        const maximumEnd = nextAssignment ? nextAssignment.startLine - 1 : Math.max(lineCount - 1, 0);
        nextEnd = Math.min(maximumEnd, Math.max(lineIndex, assignment.startLine));
    }

    const updatedAssignments = normalizedAssignments.map((currentAssignment, index) => (
        index === assignmentIndex
            ? {
                ...currentAssignment,
                startLine: nextStart,
                endLine: nextEnd,
            }
            : currentAssignment
    ));

    return {
        nextAssignments: updatedAssignments,
        nextActiveResize: {
            code: assignment.code,
            startLine: nextStart,
            endLine: nextEnd,
            edge,
        },
    };
};

export const moveAssignmentRange = (
    currentAssignments: FieldAssignment[],
    targetAssignment: Pick<FieldAssignment, 'code' | 'startLine' | 'endLine'>,
    lineIndex: number,
    pointerOffset: number,
    lineCount: number
): { nextAssignments: FieldAssignment[]; nextActiveMove: ActiveMoveState | null } => {
    const normalizedAssignments = normalizeAssignments(currentAssignments, lineCount);
    const assignmentIndex = normalizedAssignments.findIndex((assignment) => isSameAssignment(assignment, targetAssignment));

    if (assignmentIndex === -1) {
        return { nextAssignments: normalizedAssignments, nextActiveMove: null };
    }

    const assignment = normalizedAssignments[assignmentIndex];
    const previousAssignment = assignmentIndex > 0 ? normalizedAssignments[assignmentIndex - 1] : null;
    const nextAssignment = assignmentIndex < normalizedAssignments.length - 1 ? normalizedAssignments[assignmentIndex + 1] : null;
    const assignmentLength = assignment.endLine - assignment.startLine;
    const minimumStart = previousAssignment ? previousAssignment.endLine + 1 : 0;
    const maximumStart = nextAssignment
        ? Math.max(previousAssignment ? previousAssignment.endLine + 1 : 0, nextAssignment.startLine - assignmentLength - 1)
        : Math.max(lineCount - assignmentLength - 1, minimumStart);
    const proposedStart = lineIndex - pointerOffset;
    const nextStart = Math.max(minimumStart, Math.min(proposedStart, maximumStart));
    const nextEnd = nextStart + assignmentLength;

    const updatedAssignments = normalizedAssignments.map((currentAssignment, index) => (
        index === assignmentIndex
            ? {
                ...currentAssignment,
                startLine: nextStart,
                endLine: nextEnd,
            }
            : currentAssignment
    ));

    return {
        nextAssignments: updatedAssignments,
        nextActiveMove: {
            code: assignment.code,
            startLine: nextStart,
            endLine: nextEnd,
            pointerOffset,
        },
    };
};
